import { useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

interface DeployedUrlButtonsProps {
  /** The deployed site URL (e.g., 'https://docs.heatherstone.com') */
  deployedUrl: string;
}

/**
 * Buttons to open the current page on the deployed site and copy the deployed URL.
 * Useful when working locally to share links that work for others.
 */
export function DeployedUrlButtons({ deployedUrl }: DeployedUrlButtonsProps) {
  const location = useLocation();
  const [copied, setCopied] = useState(false);

  // Build the full deployed URL for the current path
  const getDeployedFullUrl = useCallback(() => {
    const base = deployedUrl.replace(/\/$/, ''); // Remove trailing slash if present
    return `${base}${location.pathname}${location.search}${location.hash}`;
  }, [deployedUrl, location]);

  const handleOpenDeployed = useCallback(() => {
    window.open(getDeployedFullUrl(), '_blank');
  }, [getDeployedFullUrl]);

  const handleCopyUrl = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(getDeployedFullUrl());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  }, [getDeployedFullUrl]);

  return (
    <div className="flex items-center gap-1">
      {/* Open on deployed site */}
      <button
        onClick={handleOpenDeployed}
        className="inline-flex items-center justify-center rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-50"
        title="Open on deployed site"
      >
        <svg
          className="h-4 w-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
          />
        </svg>
        <span className="sr-only">Open on deployed site</span>
      </button>

      {/* Copy deployed URL */}
      <button
        onClick={handleCopyUrl}
        className="inline-flex items-center justify-center rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-50"
        title={copied ? 'Copied!' : 'Copy deployed URL'}
      >
        {copied ? (
          <svg
            className="h-4 w-4 text-green-600 dark:text-green-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        ) : (
          <svg
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"
            />
          </svg>
        )}
        <span className="sr-only">
          {copied ? 'Copied!' : 'Copy deployed URL'}
        </span>
      </button>
    </div>
  );
}
